import { useEffect, useState } from "react";
import { api, post, type Row } from "../api";

type Proposal = {
  key: string;
  label: string;
  occurrence: number;
  type: string;
  value: unknown;
  raw: string;
  confidence: number;
  source: { regions: { pageNumber: number; polygon: number[] }[] };
};
const fieldId = (p: Proposal) => `${p.key}:${p.occurrence}`;
function displayValue(p: Proposal) {
  if (p.value === null) return "";
  if (p.type === "money") {
    const s = String(p.value).padStart(3, "0");
    return s.slice(0, -2) + "." + s.slice(-2);
  }
  return Array.isArray(p.value) ? p.value.join("\n") : String(p.value);
}

/** No proposal is saved on typing, selection, refresh, upload, or background
 * completion. Only the explicit review submission calls the confirmation API. */
export function W2ExtractionReview({
  returnId,
  revision,
  canReview,
  documents = [],
  onSaved,
}: {
  returnId: string;
  revision: number;
  canReview: boolean;
  documents?: Row[];
  onSaved: () => Promise<unknown>;
}) {
  const [jobs, setJobs] = useState<Row[]>([]),
    [detail, setDetail] = useState<Row | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const [choices, setChoices] = useState<Record<string, string>>({}),
    [edits, setEdits] = useState<Record<string, string>>({});
  const [affirm, setAffirm] = useState(false),
    [relationship, setRelationship] = useState(""),
    [related, setRelated] = useState(""),
    [note, setNote] = useState("");
  const [submitted, setSubmitted] = useState(false),
    [openedRevision, setOpenedRevision] = useState(revision);
  const [documentId, setDocumentId] = useState(""),
    [corrected, setCorrected] = useState("");
  const [processingMode, setProcessingMode] = useState('LOADING');
  const base = `/cases/${returnId}/w2-extractions`;
  useEffect(() => {
    let active = true;
    api(base)
      .then((data) => {
        if (active) {setJobs(data.jobs);setProcessingMode(data.mode);}
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
    };
  }, [base]);
  async function openJob(id: string) {
    setBusy(true);
    setError("");
    try {
      const data = await api(`${base}/${id}`);
      setDetail(data);
      setChoices({});
      setEdits({});
      setAffirm(false);
      setRelationship("");
      setRelated("");
      setNote("");
      setSubmitted(false);
      setOpenedRevision(revision);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const proposals = (detail?.result?.proposals || []) as Proposal[];
  const missing = proposals.filter((p) => !choices[fieldId(p)]);
  const editable =
    canReview && !detail?.reviewed && detail?.status === "succeeded";
  async function save() {
    setSubmitted(true);
    setError("");
    if (
      !editable ||
      missing.length ||
      !affirm ||
      !relationship ||
      note.trim().length < 10 ||
      (relationship === "REPLACES" && !related)
    )
      return;
    setBusy(true);
    try {
      await post(`${base}/${detail?.jobId}/confirm`, {
        expectedRevision: openedRevision,
        confirmSourceReviewed: affirm,
        relationship,
        relatedRecordId: relationship === "REPLACES" ? related : null,
        note,
        decisions: proposals.map((p) => ({
          key: p.key,
          occurrence: p.occurrence,
          disposition: choices[fieldId(p)],
          ...(choices[fieldId(p)] === "CORRECTED"
            ? {
                value:
                  p.type === "lines"
                    ? (edits[fieldId(p)] || "").split("\n")
                    : edits[fieldId(p)] || "",
              }
            : {}),
        })),
      });
      await onSaved();
      setDetail(null);
      setJobs((await api(base)).jobs);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="card spaced" aria-label="W-2 document review">
      <h2>Review information from your W-2</h2>
      <p>
        Automatic processing is not approved for real taxpayer documents. Only
        explicitly approved synthetic test documents may be processed. Review
        every proposed value before saving it to the return.
      </p>
      {canReview && (
        <fieldset disabled={busy}>
          <legend>Choose a W-2 document</legend>
          <label>
            Security-cleared document
            <select
              value={documentId}
              onChange={(e) => setDocumentId(e.target.value)}
            >
              <option value="">Choose the W-2 to review</option>
              {documents
                .filter(
                  (d) =>
                    d.scanStatus === "CLEAN" &&
                    ["application/pdf", "image/png", "image/jpeg"].includes(
                      d.mimeType,
                    ),
                )
                .map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Is this a corrected or replacement statement?
            <select
              value={corrected}
              onChange={(e) => setCorrected(e.target.value)}
            >
              <option value="">Choose one</option>
              <option value="no">No</option>
              <option value="yes">Yes — compare with the earlier W-2</option>
            </select>
          </label>
          <button
            type="button"
            className="btn"
            disabled={!documentId || !corrected || busy || processingMode !== 'AZURE_SYNTHETIC'}
            onClick={async () => {
              setBusy(true);
              setError("");
              try {
                await post(base, {
                  documentId,
                  documentType: "W-2",
                  corrected: corrected === "yes",
                });
                setJobs((await api(base)).jobs);
              } catch (e) {
                setError((e as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Request W-2 processing
          </button>
          <p>
            {processingMode === 'AZURE_SYNTHETIC'
              ? 'Azure processing is configured for specifically approved synthetic W-2 documents. Each document’s authorization and security scan are checked before transmission. All extracted values require your review.'
              : processingMode === 'LOADING'
                ? 'Checking document-processing availability…'
                : 'Azure document processing is not active in this environment. Uploads remain stored; no extracted values are being generated.'}
          </p>
        </fieldset>
      )}
      <button
        type="button"
        className="btn"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setError("");
          try {
            const state=await api(base);setJobs(state.jobs);setProcessingMode(state.mode);
          } catch (e) {
            setError((e as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        Refresh processing status
      </button>
      {error && (
        <p role="alert" className="error-text">
          W-2 review: {error}
        </p>
      )}
      {!jobs.length && (
        <p>
          No W-2 processing requests. You can continue entering information
          manually.
        </p>
      )}
      <ul>
        {jobs.map((j) => (
          <li key={j.jobId}>
            {j.reviewed
              ? "Reviewed and saved"
              : j.status === "succeeded"
                ? "Ready for your review"
                : j.status === "failed"
                  ? "Processing needs attention"
                  : j.status === "cancelled"
                    ? "Processing cancelled"
                    : "Waiting for processing"}{" "}
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() => void openJob(j.jobId)}
            >
              View W-2 review
            </button>
            {canReview && ["queued", "running"].includes(j.status) && (
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  setError("");
                  try {
                    await post(`${base}/${j.jobId}/cancel`);
                    setJobs((await api(base)).jobs);
                    setDetail(null);
                  } catch (e) {
                    setError((e as Error).message);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Cancel processing
              </button>
            )}
          </li>
        ))}
      </ul>
      {detail && (
        <div>
          <a
            className="btn"
            href={`/api/cases/${returnId}/evidence/${detail.documentId}/content`}
            target="_blank"
            rel="noreferrer"
          >
            Open original W-2
          </a>
          {detail.status !== "succeeded" && (
            <p>
              The document has no completed extraction to review. Your return
              information has not changed.
            </p>
          )}
          {(detail.correctedDeclared ||
            detail.result?.warnings?.includes("POSSIBLE_CORRECTION")) && (
            <p role="status">
              This W-2 may correct an earlier statement. Compare both documents
              and select the statement being replaced. Amounts are never
              combined automatically.
            </p>
          )}
          {detail.result && (
            <>
              <p>
                Check every field against the original. Blank or uncertain
                readings are not assumed to be zero.
              </p>
              {submitted &&
                (missing.length > 0 ||
                  !affirm ||
                  !relationship ||
                  note.trim().length < 10) && (
                  <div role="alert" className="validation-summary">
                    <h3>W-2 review — information still needed</h3>
                    <ul>
                      {missing.map((p) => (
                        <li key={fieldId(p)}>
                          <a href={`#w2-review-${fieldId(p)}`}>
                            {p.label}: choose Confirm, Correct, or Blank after
                            checking the document.
                          </a>
                        </li>
                      ))}
                      {!relationship && (
                        <li>
                          <a href="#w2-relationship">
                            Choose how this W-2 relates to other statements.
                          </a>
                        </li>
                      )}
                      {note.trim().length < 10 && (
                        <li>
                          <a href="#w2-review-note">
                            Describe your source check in at least 10
                            characters.
                          </a>
                        </li>
                      )}
                      {!affirm && (
                        <li>
                          <a href="#w2-reviewed-source">
                            Confirm that you compared all fields with the
                            original.
                          </a>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              {[
                "Employer and employee",
                "Wages and withholding",
                "Other boxes",
              ].map((group, index) => (
                <details key={group} open={index === 0}>
                  <summary>{group}</summary>
                  {proposals
                    .filter((p) =>
                      index === 0
                        ? /^W2\.(?:meta|[a-f])\./.test(p.key)
                        : index === 1
                          ? /^W2\.[1-8]$/.test(p.key)
                          : !/^W2\.(?:meta|[a-f])\./.test(p.key) &&
                            !/^W2\.[1-8]$/.test(p.key),
                    )
                    .map((p) => {
                      const key = fieldId(p),
                        invalid = submitted && !choices[key],
                        selectId = `w2-review-${key}`;
                      return (
                        <fieldset
                          key={key}
                          className="spaced"
                          disabled={!editable || busy}
                        >
                          <legend>
                            {p.label}
                            {p.occurrence > 0
                              ? ` — row ${p.occurrence + 1}`
                              : ""}
                          </legend>
                          <p>
                            Proposed value:{" "}
                            <strong>
                              {displayValue(p) ||
                                "Not read — check the document"}
                            </strong>
                          </p>
                          <p>
                            {p.confidence < 0.9
                              ? "Uncertain reading — please check carefully."
                              : "Review required before saving."}{" "}
                            {p.source.regions.length
                              ? `Source: page ${p.source.regions[0].pageNumber}.`
                              : "No reliable source location was found."}
                          </p>
                          {p.raw && (
                            <p>
                              Text read from document: <q>{p.raw}</q>
                            </p>
                          )}
                          <label htmlFor={selectId}>
                            Your review of {p.label}
                          </label>
                          <select
                            id={selectId}
                            value={choices[key] || ""}
                            onChange={(e) =>
                              setChoices({ ...choices, [key]: e.target.value })
                            }
                            aria-invalid={invalid}
                            aria-describedby={
                              invalid ? selectId + "-error" : undefined
                            }
                            style={
                              invalid ? { borderColor: "#b42318" } : undefined
                            }
                          >
                            <option value="">Choose after reviewing</option>
                            {p.value !== null && (
                              <option value="CONFIRMED">
                                Confirm the proposed value
                              </option>
                            )}
                            <option value="CORRECTED">
                              Enter the correct value
                            </option>
                            <option value="BLANK">
                              The field is blank on the document
                            </option>
                          </select>
                          {invalid && (
                            <p id={selectId + "-error"} className="error-text">
                              {p.label}: confirm, correct, or mark blank before
                              saving.
                            </p>
                          )}
                          {choices[key] === "CORRECTED" && (
                            <label>
                              Correct value for {p.label}
                              <input
                                value={edits[key] || ""}
                                onChange={(e) =>
                                  setEdits({ ...edits, [key]: e.target.value })
                                }
                                autoComplete="off"
                              />
                            </label>
                          )}
                        </fieldset>
                      );
                    })}
                </details>
              ))}
              <label htmlFor="w2-relationship">
                How does this W-2 relate to other statements?
              </label>
              <select
                id="w2-relationship"
                value={relationship}
                disabled={!editable || busy}
                onChange={(e) => setRelationship(e.target.value)}
              >
                <option value="">Choose one</option>
                <option value="NEW_EMPLOYER">
                  First statement from this employer
                </option>
                <option value="ADDITIONAL_STATEMENT">
                  Additional, separate statement — I checked it is not a
                  duplicate
                </option>
                <option value="REPLACES">
                  Corrects or replaces a reviewed statement
                </option>
              </select>
              {relationship === "REPLACES" && (
                <label>
                  Reviewed statement being replaced
                  <select
                    value={related}
                    disabled={!editable || busy}
                    onChange={(e) => setRelated(e.target.value)}
                  >
                    <option value="">Select the earlier statement</option>
                    {detail.reviewedRecords.map((r: Row, i: number) => (
                      <option key={r.id} value={r.id}>
                        Reviewed W-2 {i + 1} —{" "}
                        {r.fields.find((f: Row) => f.key === "W2.c.name")
                          ?.value || "Employer"}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label htmlFor="w2-review-note">
                Source check and corrections
              </label>
              <textarea
                id="w2-review-note"
                value={note}
                disabled={!editable || busy}
                maxLength={2000}
                onChange={(e) => setNote(e.target.value)}
              />
              <label>
                <input
                  id="w2-reviewed-source"
                  type="checkbox"
                  checked={affirm}
                  disabled={!editable || busy}
                  onChange={(e) => setAffirm(e.target.checked)}
                />
                I compared every field with the original W-2 and reviewed any
                duplicate or corrected statements.
              </label>
              {editable && (
                <button
                  type="button"
                  className="btn primary"
                  disabled={busy}
                  onClick={() => void save()}
                >
                  {busy ? "Saving review…" : "Confirm review and save W-2"}
                </button>
              )}
              {detail.reviewed && (
                <p>
                  This review has been saved. Its original evidence is
                  preserved.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
